// packages/web/src/process/useLayoutParam.ts
//
// URL hash reader/writer for the process-view layout param.
// Layouts: flow (causal swimlane), sequence (UML sequence).
// Legacy "swimlane" maps to "flow" for backward compat.

import { useCallback, useEffect, useState } from "react";

export type ProcessLayout = "flow" | "sequence" | "tree" | "bpmn";

const LAYOUT_KEY = "layout";
const DEFAULT_LAYOUT: ProcessLayout = "flow";

function parseLayout(hash: string): ProcessLayout {
  const m = hash.match(/[#&]layout=([^&]*)/);
  if (!m) return DEFAULT_LAYOUT;
  const v = decodeURIComponent(m[1] ?? "");
  if (v === "sequence") return "sequence";
  if (v === "tree") return "tree";
  if (v === "bpmn") return "bpmn";
  if (v === "flow" || v === "swimlane") return "flow";
  return DEFAULT_LAYOUT;
}

function writeLayoutToHash(layout: ProcessLayout): string {
  const hash = window.location.hash.replace(/^#/, "");
  const params = new URLSearchParams(hash);
  params.set(LAYOUT_KEY, layout);
  return "#" + params.toString();
}

function readLayout(): ProcessLayout {
  if (typeof window === "undefined") return DEFAULT_LAYOUT;
  return parseLayout(window.location.hash);
}

export function useLayoutParam(): {
  layout: ProcessLayout;
  setLayout: (l: ProcessLayout) => void;
} {
  const [layout, setLayoutState] = useState<ProcessLayout>(readLayout);

  useEffect(() => {
    const onHashChange = () => setLayoutState(readLayout());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const setLayout = useCallback((l: ProcessLayout) => {
    setLayoutState(l);
    window.location.hash = writeLayoutToHash(l);
  }, []);

  return { layout, setLayout };
}
